import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const rejectApiError = (error, rejectWithValue) => {
  const data = error?.response?.data
  if (typeof data === 'string') {
    return rejectWithValue({ error: data })
  }
  return rejectWithValue({
    error: data?.errors || data?.error || error.message || 'Request failed',
  })
}

const emptyInspector = () => ({
  notes: [],
  discrepancies: [],
  files: [],
  onHandCounts: [],
  amendCounts: [],
  boxScanCounts: [],
  activity: [],
  loading: false,
})

const initialState = {
  loading: false,
  error: null,
  logs: [],
  logsLoading: false,
  inspectorByPoId: {},
  uploading: false,
}

/** GET /api/v1/shipments — receiving queue page */
export const getReceivingShipmentsRequest = createAsyncThunk(
  'boxReceived/getReceivingShipmentsRequest',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/shipments', { params })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** GET /api/v1/receiving/queue — lean receiving queue page */
export const getReceivingQueueRequest = createAsyncThunk(
  'boxReceived/getReceivingQueueRequest',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/receiving/queue', { params })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** GET /api/v1/po-received-logs */
export const getPoReceivedLogsRequest = createAsyncThunk(
  'boxReceived/getPoReceivedLogsRequest',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/po-received-logs', { params })
      return Array.isArray(data) ? data : []
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** GET /api/v1/po-received-logs/search-po */
export const searchPoReceivedLogRequest = createAsyncThunk(
  'boxReceived/searchPoReceivedLogRequest',
  async ({ po_number }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/po-received-logs/search-po', {
        params: { po_number },
      })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/po-received-logs */
export const createPoReceivedLogRequest = createAsyncThunk(
  'boxReceived/createPoReceivedLogRequest',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-logs', payload)
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/purchase-orders/:id/status */
export const updatePurchaseOrderStatusRequest = createAsyncThunk(
  'boxReceived/updatePurchaseOrderStatusRequest',
  async ({ purchaseOrderId, status }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/purchase-orders/${purchaseOrderId}/status`, {
        status,
      })
      return { purchaseOrderId, status, data }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** Load notes + discrepancies + files for PoInspector */
export const getPoInspectorExtrasRequest = createAsyncThunk(
  'boxReceived/getPoInspectorExtrasRequest',
  async ({ purchaseOrderId }, { rejectWithValue }) => {
    try {
      const [discRes, notesRes, filesRes, onHandRes, amendRes, scanRes, activityRes] =
        await Promise.all([
        axios.get('/api/v1/po-received-boxes/discrepancies', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/notes', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/files', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/on-hand-counts', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/amend-counts', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/scan-counts', {
          params: { purchaseOrderId },
        }),
        axios.get('/api/v1/po-received-boxes/activity', {
          params: { purchaseOrderId },
        }),
      ])
      return {
        purchaseOrderId,
        discrepancies: discRes.data?.items || [],
        notes: notesRes.data?.items || [],
        files: filesRes.data?.items || [],
        onHandCounts: onHandRes.data?.items || [],
        amendCounts: amendRes.data?.items || [],
        boxScanCounts: scanRes.data?.items || [],
        activity: activityRes.data?.items || [],
      }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const createPoDiscrepancyRequest = createAsyncThunk(
  'boxReceived/createPoDiscrepancyRequest',
  async ({ purchaseOrderId, description }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-boxes/discrepancies', {
        purchaseOrderId,
        description,
      })
      return { purchaseOrderId, item: data.item }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const createPoReceivedBoxesNoteRequest = createAsyncThunk(
  'boxReceived/createPoReceivedBoxesNoteRequest',
  async ({ purchaseOrderId, description }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-boxes/notes', {
        purchaseOrderId,
        description,
      })
      return { purchaseOrderId, item: data.item }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const getPoReceivedBoxesFilesRequest = createAsyncThunk(
  'boxReceived/getPoReceivedBoxesFilesRequest',
  async ({ purchaseOrderId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/po-received-boxes/files', {
        params: { purchaseOrderId },
      })
      return { purchaseOrderId, items: data.items || [] }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const uploadPoReceivedBoxesFileRequest = createAsyncThunk(
  'boxReceived/uploadPoReceivedBoxesFileRequest',
  async ({ purchaseOrderId, file, tempId }, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('referenceType', 'PurchaseOrder')
      formData.append('referenceId', purchaseOrderId)
      const { data } = await axios.post('/api/v1/po-received-boxes/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return { purchaseOrderId, file: data.file, tempId }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const deletePoReceivedBoxesFileRequest = createAsyncThunk(
  'boxReceived/deletePoReceivedBoxesFileRequest',
  async ({ purchaseOrderId, fileId }, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/v1/po-received-boxes/files/${fileId}`)
      return { purchaseOrderId, fileId }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/box-received-logs — RFID scan amount for a box */
export const createBoxReceivedLogRequest = createAsyncThunk(
  'boxReceived/createBoxReceivedLogRequest',
  async (
    {
      purchaseOrderId,
      boxId,
      dateReceived,
      epcHexTags,
      allowMixedSku,
      allowOverReceipt,
    },
    { rejectWithValue },
  ) => {
    try {
      // Server-authoritative: totals are computed from the EPC rows, no
      // client-supplied amount is sent.
      const { data } = await axios.post('/api/v1/box-received-logs', {
        box_id: boxId,
        date_received: dateReceived || new Date().toISOString(),
        received: true,
        epc_hex_tags: epcHexTags || [],
        allowMixedSku: allowMixedSku === true,
        allowOverReceipt: allowOverReceipt === true,
      })
      return {
        purchaseOrderId,
        boxId,
        amountReceived: data.amount_received ?? data.scannedTotal ?? data.total_matched,
        logId: data.id,
        packerName: data.packer?.full_name || null,
        createdAt: data.created_at || data.createdAt || new Date().toISOString(),
        autoReceived: !!data.autoReceived,
        boxReceived: data.boxReceived,
        scannedTotal: data.scannedTotal,
        previousScanned: data.previousScanned,
        classification: data.classification,
        duplicates: data.duplicates || [],
        total_matched: data.total_matched,
        total_scanned: data.total_scanned,
      }
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/po-received-boxes/apply-received — clean-scan auto-receive without audit row */
export const applyInboundReceivedRequest = createAsyncThunk(
  'boxReceived/applyInboundReceivedRequest',
  async ({ purchaseOrderId, boxId, quantity, inboundType }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-boxes/apply-received', {
        purchaseOrderId,
        boxId,
        quantity,
        inboundType,
      })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/po-received-boxes/on-hand-counts */
export const createPoOnHandCountRequest = createAsyncThunk(
  'boxReceived/createPoOnHandCountRequest',
  async (
    { purchaseOrderId, boxId, quantity, source, scannedQuantity, inboundType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-boxes/on-hand-counts', {
        purchaseOrderId,
        boxId,
        quantity,
        source,
        scannedQuantity,
        inboundType,
      })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

/** POST /api/v1/po-received-boxes/amend-counts */
export const createPoAmendCountRequest = createAsyncThunk(
  'boxReceived/createPoAmendCountRequest',
  async (
    { purchaseOrderId, boxId, amendedQuantity, previousQuantity, reason, inboundType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await axios.post('/api/v1/po-received-boxes/amend-counts', {
        purchaseOrderId,
        boxId,
        amendedQuantity,
        previousQuantity,
        reason,
        inboundType,
      })
      return data
    } catch (error) {
      return rejectApiError(error, rejectWithValue)
    }
  }
)

export const boxReceivedSlice = createSlice({
  name: 'boxReceived',
  initialState,
  reducers: {
    clearPoInspectorExtras(state, action) {
      const purchaseOrderId = action.payload
      if (purchaseOrderId) {
        delete state.inspectorByPoId[purchaseOrderId]
      } else {
        state.inspectorByPoId = {}
      }
    },
    setPoInspectorFiles(state, action) {
      const { purchaseOrderId, files } = action.payload
      if (!state.inspectorByPoId[purchaseOrderId]) {
        state.inspectorByPoId[purchaseOrderId] = emptyInspector()
      }
      state.inspectorByPoId[purchaseOrderId].files = files
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPoReceivedLogsRequest.pending, (state) => {
        state.logsLoading = true
        state.error = null
      })
      .addCase(getPoReceivedLogsRequest.fulfilled, (state, action) => {
        state.logsLoading = false
        state.logs = action.payload
      })
      .addCase(getPoReceivedLogsRequest.rejected, (state, action) => {
        state.logsLoading = false
        state.error = action.payload?.error || 'Failed to load logs'
        state.logs = []
      })

      .addCase(getPoInspectorExtrasRequest.pending, (state, action) => {
        const purchaseOrderId = action.meta.arg.purchaseOrderId
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].loading = true
        state.error = null
      })
      .addCase(getPoInspectorExtrasRequest.fulfilled, (state, action) => {
        const {
          purchaseOrderId,
          notes,
          discrepancies,
          files,
          onHandCounts,
          amendCounts,
          boxScanCounts,
          activity,
        } = action.payload
        state.inspectorByPoId[purchaseOrderId] = {
          notes,
          discrepancies,
          files,
          onHandCounts: onHandCounts || [],
          amendCounts: amendCounts || [],
          boxScanCounts: boxScanCounts || [],
          activity: activity || [],
          loading: false,
        }
      })
      .addCase(getPoInspectorExtrasRequest.rejected, (state, action) => {
        const purchaseOrderId = action.meta.arg.purchaseOrderId
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].loading = false
        state.error = action.payload?.error || 'Failed to load inspector data'
      })

      .addCase(createPoDiscrepancyRequest.fulfilled, (state, action) => {
        const { purchaseOrderId, item } = action.payload
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].discrepancies = [
          item,
          ...(state.inspectorByPoId[purchaseOrderId].discrepancies || []),
        ]
        state.inspectorByPoId[purchaseOrderId].activity = [
          {
            id: `discrepancy-${item.id}`,
            kind: 'exception',
            action: 'Discrepancy flagged',
            detail: item.description,
            actor: 'Operator',
            timestamp: item.createdAt || new Date().toISOString(),
          },
          ...(state.inspectorByPoId[purchaseOrderId].activity || []),
        ]
      })

      .addCase(createPoReceivedBoxesNoteRequest.fulfilled, (state, action) => {
        const { purchaseOrderId, item } = action.payload
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].notes = [
          item,
          ...(state.inspectorByPoId[purchaseOrderId].notes || []),
        ]
        state.inspectorByPoId[purchaseOrderId].activity = [
          {
            id: `note-${item.id}`,
            kind: 'note',
            action: 'Note added',
            detail: item.description,
            actor: item.authorName || 'Unknown',
            timestamp: item.createdAt || new Date().toISOString(),
          },
          ...(state.inspectorByPoId[purchaseOrderId].activity || []),
        ]
      })

      .addCase(getPoReceivedBoxesFilesRequest.fulfilled, (state, action) => {
        const { purchaseOrderId, items } = action.payload
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].files = items
      })

      .addCase(uploadPoReceivedBoxesFileRequest.pending, (state) => {
        state.uploading = true
      })
      .addCase(uploadPoReceivedBoxesFileRequest.fulfilled, (state, action) => {
        state.uploading = false
        const { purchaseOrderId, file, tempId } = action.payload
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        const files = state.inspectorByPoId[purchaseOrderId].files || []
        const idx = files.findIndex((f) => f.id === tempId)
        if (idx >= 0) {
          files[idx] = { ...file, loading: false }
        } else {
          files.unshift({ ...file, loading: false })
        }
        state.inspectorByPoId[purchaseOrderId].files = [...files]
        state.inspectorByPoId[purchaseOrderId].activity = [
          {
            id: `file-${file.id}`,
            kind: 'doc',
            action: 'Document attached',
            detail: file.originalName || file.original_name || null,
            actor: file.authorName || 'Unknown',
            timestamp: file.createdAt || file.created_at || new Date().toISOString(),
          },
          ...(state.inspectorByPoId[purchaseOrderId].activity || []),
        ]
      })
      .addCase(uploadPoReceivedBoxesFileRequest.rejected, (state) => {
        state.uploading = false
      })

      .addCase(deletePoReceivedBoxesFileRequest.fulfilled, (state, action) => {
        const { purchaseOrderId, fileId } = action.payload
        const bucket = state.inspectorByPoId[purchaseOrderId]
        if (!bucket) return
        bucket.files = (bucket.files || []).filter((f) => f.id !== fileId)
      })

      .addCase(createPoOnHandCountRequest.fulfilled, (state, action) => {
        const purchaseOrderId = action.meta.arg.purchaseOrderId
        const item = action.payload?.item
        if (!item) return
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].onHandCounts = [
          item,
          ...(state.inspectorByPoId[purchaseOrderId].onHandCounts || []),
        ]
      })

      .addCase(createPoAmendCountRequest.fulfilled, (state, action) => {
        const purchaseOrderId = action.meta.arg.purchaseOrderId
        const item = action.payload?.item
        if (!item) return
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        state.inspectorByPoId[purchaseOrderId].amendCounts = [
          item,
          ...(state.inspectorByPoId[purchaseOrderId].amendCounts || []),
        ]
      })

      .addCase(createBoxReceivedLogRequest.fulfilled, (state, action) => {
        const { purchaseOrderId, boxId, amountReceived, packerName, logId, createdAt } =
          action.payload || {}
        if (!purchaseOrderId || boxId == null) return
        if (!state.inspectorByPoId[purchaseOrderId]) {
          state.inspectorByPoId[purchaseOrderId] = emptyInspector()
        }
        const existing = (state.inspectorByPoId[purchaseOrderId].boxScanCounts || []).filter(
          (s) => String(s.boxId) !== String(boxId),
        )
        state.inspectorByPoId[purchaseOrderId].boxScanCounts = [
          { boxId, amountReceived, packerName, logId, createdAt },
          ...existing,
        ]
      })
  },
})

export const { clearPoInspectorExtras, setPoInspectorFiles } = boxReceivedSlice.actions

export default boxReceivedSlice.reducer
